import { formatSIWithUnit } from "../../../utils/properties";
import {
  Card,
  FilterSearch,
  FilterSearchInput,
  FilterValueList,
  FilterValueListItem,
  Header,
  Label,
} from "./FilterStyles";
import {
  PropertyAggregateBucket,
  ParameterAggregate,
  addToPendingFilters,
  removeFromPendingFilters,
} from "../../../reducers/componentSearch";
import Checkbox from "../../common/Checkbox";
import { COLORS } from "../../../constants/Styles";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/store";
import { Parameter } from "../../../reducers/models/parameters";
import { BindString, useStringInput } from "../../../utils/hooks/inputs";
import _ from "lodash";

export const SelectableFilterItem = ({
  parameter,
  bucket,
  isLast,
}: {
  parameter: Parameter;
  bucket: PropertyAggregateBucket;
  isLast: boolean;
}) => {
  const rawValue =
    bucket.floatValue !== null
      ? bucket.floatValue.toString()
      : bucket.displayValue;
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.componentSearch.loading);
  const isSelected = useAppSelector(
    (state) =>
      state.componentSearch.pendingFilters[parameter.short_name]?.includes(
        rawValue,
      ) || false,
  );
  const getBucketDisplayValue = () => {
    if (bucket.floatValue !== null) {
      return formatSIWithUnit(bucket.floatValue, parameter.unit || "");
    }
    return bucket.displayValue;
  };

  const count =
    bucket.count !== null ? (
      <span style={{ color: COLORS.secondaryLight, marginLeft: "0.5rem" }}>
        ({bucket.count.toLocaleString()})
      </span>
    ) : null;

  const label = (
    <span>
      {getBucketDisplayValue()}
      {count}
    </span>
  );

  const onChange = () => {
    if (isSelected) {
      dispatch(
        removeFromPendingFilters({
          parameterShortName: parameter.short_name,
          rawValue,
        }),
      );
    } else {
      dispatch(
        addToPendingFilters({
          parameterShortName: parameter.short_name,
          rawValue,
        }),
      );
    }
  };

  return (
    <FilterValueListItem
      key={bucket.displayValue}
      isLastElement={isLast}
      isSelected={isSelected}
    >
      <Checkbox
        checked={isSelected}
        onChange={onChange}
        label={label}
        disabled={loading}
      />
    </FilterValueListItem>
  );
};

function FilterHeader({
  parameterAggregate,
  bindSearch,
}: {
  parameterAggregate: ParameterAggregate;
  bindSearch: BindString;
}) {
  const numSelected = useAppSelector(
    (state) =>
      state.componentSearch.pendingFilters[
        parameterAggregate.parameter.short_name
      ]?.length || 0,
  );

  return (
    <Header>
      <Label>
        <div>{parameterAggregate.property_aggregate.attribute.name}</div>
        {numSelected > 0 && <div>{`${numSelected} selected`}</div>}
      </Label>

      <FilterSearch>
        <FilterSearchInput placeholder={"Filter values"} {...bindSearch} />
      </FilterSearch>
    </Header>
  );
}

export default function Filter({
  parameterAggregate,
}: {
  parameterAggregate: ParameterAggregate;
}) {
  const { value: search, bind: bindSearch } = useStringInput("");
  const maybeNumber = parseFloat(search);
  const searchIsNumber = !isNaN(maybeNumber);
  const lowerCaseSearch = search.toLowerCase();
  const sortedBuckets = _.sortBy(
    parameterAggregate.property_aggregate.buckets,
    "floatValue",
  );

  const filteredBuckets = sortedBuckets.filter((bucket) => {
    if (search) {
      if (bucket.floatValue !== null && searchIsNumber) {
        return (
          bucket.floatValue == maybeNumber ||
          bucket.displayValue.toLowerCase().startsWith(lowerCaseSearch)
        );
      }
      return bucket.displayValue.toLowerCase().includes(lowerCaseSearch);
    }
    return true;
  });

  return (
    <Card>
      <FilterHeader
        parameterAggregate={parameterAggregate}
        bindSearch={bindSearch}
      />
      <FilterValueList>
        {filteredBuckets.map((bucket, index) => {
          return (
            <SelectableFilterItem
              key={`${parameterAggregate.parameter.id}-${bucket.displayValue}-${index}`}
              parameter={parameterAggregate.parameter}
              bucket={bucket}
              isLast={
                index ==
                parameterAggregate.property_aggregate.buckets.length - 1
              }
            />
          );
        })}
      </FilterValueList>
    </Card>
  );
}
